import React, { useState, useMemo, useEffect, useRef } from "react";
import { Row, Col, Table, Badge, Input, Offcanvas, OffcanvasHeader, OffcanvasBody, ButtonGroup, Button, UncontrolledTooltip } from "reactstrap";
import { customersReport } from "../service";
import OrgSummaryPane from "./OrgSummaryPane";
import staticData from "../data/customers_sample.json";
import { getRelativeTime } from "../../common/DateHelpers";

const USE_STATIC_DATA = process.env.REACT_APP_ENVIRONMENT === "production" ? false : true;
const formatCurrency = (value) => `₹${(value / 100000).toFixed(2)}L`;

const sizeToAmov = {
  s: 1.5,
  m: 3,
  l: 5,
  xl: 7,
  xxl: 10,
};

const getMonthYearString = (date) =>
  date.toLocaleString("default", { month: "short", year: "numeric" });

const getMonthYearDisplay = (date) => {
  return `${date.toLocaleString("default", { month: "short" })} '${date
    .getFullYear()
    .toString()
    .slice(-2)}`;
};

const getMonthRange = (start, end) => {
  const range = [];
  const startDate = new Date(start);
  const endDate = new Date(end);
  startDate.setDate(1);
  while (startDate <= endDate) {
    range.push(getMonthYearString(new Date(startDate)));
    startDate.setMonth(startDate.getMonth() + 1);
  }
  return range;
};

const transformRestructuredData = (data) => {
  const { organizations, orders, pipeline_projects = [] } = data;

  const orderItems = orders.map((order) => {
    const org = organizations[order.organization_id?.toString()];
    return {
      organization_id: order.organization_id,
      organization_name: org?.name,
      organization_status: org?.status,
      project_id: order.project_id,
      project_name: order.project_name,
      order_id: order.order_id,
      order_status: order.order_status,
      placed_date: order.placed_date,
      booked_date: order.booked_date,
      last_activity_at: org?.last_activity_at,
      value: order.value,
      order_date: order.order_date,
      size: org?.size,
      is_projection: false,
    };
  });

  const projectionItems = pipeline_projects.map((proj) => {
    const org = organizations[proj.organization_id?.toString()];
    return {
      organization_id: proj.organization_id,
      organization_name: org?.name,
      organization_status: org?.status,
      project_id: null,
      project_name: proj.name,
      order_id: null,
      order_status: null,
      placed_date: proj.month ? `${proj.month}-01` : null,
      booked_date: proj.month ? `${proj.month}-01` : null,
      last_activity_at: org?.last_activity_at,
      value: proj.value,
      order_date: proj.month ? `${proj.month}-01` : null,
      size: org?.size,
      is_projection: true,
      pipeline_project_id: proj.id,
    };
  });

  return [...orderItems, ...projectionItems];
};

const Customers = () => {
  const today = new Date();
  const defaultEnd = today.toISOString().split("T")[0];
  const defaultStart = new Date(today.getFullYear(), today.getMonth() - 5, 1)
    .toISOString()
    .split("T")[0];

  // Override list of organization IDs to mark as inactive
  const inactiveOrgIds = [275, 117, 226, 58, 460];
  // Override list of organization IDs to mark as active
  const activeOrgIds = [507];

  const [dateField, setDateField] = useState("placed_date");
  const [projectionFilter, setProjectionFilter] = useState("placed"); // "all", "placed", "projections"
  const [startDate, setStartDate] = useState(defaultStart);
  const [filters, setFilters] = useState({ size: "all" });
  const [endDate, setEndDate] = useState(defaultEnd);
  const [projectOrders, setProjectOrders] = useState([]);
  const [rawData, setRawData] = useState(null); // Store raw data for metrics calculation
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [selectedMonthData, setSelectedMonthData] = useState(null);
  const [viewMode, setViewMode] = useState("revenue"); // "revenue", "retention", "logos"
  const [sortConfig, setSortConfig] = useState({
    key: null, // null means no sorting - preserve original order
    direction: "asc",
  });
  // Store row order to preserve across view switches
  const rowOrderRef = useRef(new Map());

  const months = useMemo(
    () => getMonthRange(startDate, endDate),
    [startDate, endDate]
  );

  // Exclude current month from metric calculations (RR, LR, LG)
  const monthsForMetrics = useMemo(() => {
    const today = new Date();
    const currentMonthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    return months.filter((monthStr) => {
      const monthDate = new Date(monthStr);
      const monthKey = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;
      return monthKey !== currentMonthKey;
    });
  }, [months]);

  const effectiveProjectOrders = useMemo(() => {
    if (projectionFilter === "all") {
      return projectOrders;
    } else if (projectionFilter === "projections") {
      return projectOrders.filter((p) => p.is_projection);
    } else {
      // "placed" - default
      return projectOrders.filter((p) => !p.is_projection);
    }
  }, [projectOrders, projectionFilter]);

  useEffect(() => {
    if (USE_STATIC_DATA) {
      setRawData(staticData);
      setProjectOrders(transformRestructuredData(staticData));
    } else {
      customersReport().then((data) => {
        setRawData(data);
        setProjectOrders(transformRestructuredData(data));
      });
    }
  }, []);

  const isRepeatCustomer = (orders) => {
    const dates = orders
      .filter((o) => o.value >= 25000) // Only consider orders >= ₹25,000
      .map((o) => new Date(o[dateField]))
      .sort((a, b) => a - b);

    if (dates.length < 2) return false;

    let anchor = dates[0];
    for (let i = 1; i < dates.length; i++) {
      const diffDays = (dates[i] - anchor) / (1000 * 60 * 60 * 24);
      if (diffDays >= 7) return true;
    }
    return false;
  };

  const computeAmovValue = (group) => {
    // Look for a size from any project in the group
    const size = group.items.find((i) => i.size)?.size;
    const sizeVal = sizeToAmov[size];

    if (!sizeVal) return null;

    const hasOrder = group.items.some((i) => i.order_date && i[dateField]);
    if (!hasOrder) return null;

    return {
      amov: sizeVal * 100000,
    };
  };

  const grouped = useMemo(() => {
    if (!Array.isArray(effectiveProjectOrders)) return [];

    const map = new Map();
    effectiveProjectOrders.forEach((p) => {
      if (p.organization_status !== "customer") return;
      if (!map.has(p.organization_id)) {
        map.set(p.organization_id, {
          customer: p.organization_name,
          size: p.size,
          organization_id: p.organization_id,
          items: [],
          lastActivity: null,
        });
      }
      map.get(p.organization_id).items.push(p);
      if (
        !map.get(p.organization_id).lastActivity ||
        (p.last_activity_at &&
          new Date(p.last_activity_at) >
            new Date(map.get(p.organization_id).lastActivity))
      ) {
        map.get(p.organization_id).lastActivity = p.last_activity_at;
      }
    });

    return Array.from(map.values());
  }, [effectiveProjectOrders]);

  // Note: isActiveCustomer is defined after customerLevelMetrics to access it

  // Calculate customer-level metrics (prev_i, curr_i) for each customer and month
  // Must be defined before sortedGrouped since sortedGrouped uses it
  const customerLevelMetrics = useMemo(() => {
    if (!rawData || !rawData.organizations || !rawData.orders) return new Map();

    const { organizations, orders } = rawData;
    const customerMap = new Map();

    // Get all customers with their orders
    orders.forEach((order) => {
      if (!order.placed_date || !order.value || order.value < 50000) return;
      const org = organizations[order.organization_id?.toString()];
      if (!org || !org.demand_periodicity) return;

      const orgId = order.organization_id;
      if (!customerMap.has(orgId)) {
        customerMap.set(orgId, {
          organization_id: orgId,
          organization_name: org.name,
          demand_periodicity: org.demand_periodicity,
          orders: [],
          prev_i: {},
          curr_i: {},
          onboardedMonth: null,
          firstReactivationMonth: null,
        });
      }
      customerMap.get(orgId).orders.push({
        placed_date: order.placed_date,
        value: order.value,
      });
    });

    // Calculate prev_i and curr_i for each customer for each month
    const allMonths = months.map(m => {
      const d = new Date(m);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });

    customerMap.forEach((customer) => {
      months.forEach((monthStr) => {
        const reportingMonth = new Date(monthStr);
        const monthKey = `${reportingMonth.getFullYear()}-${String(reportingMonth.getMonth() + 1).padStart(2, '0')}`;
        const f_i = customer.demand_periodicity;

        const prevStart = new Date(reportingMonth.getFullYear(), reportingMonth.getMonth() - f_i, 1);
        prevStart.setHours(0, 0, 0, 0);
        const prevEnd = new Date(reportingMonth.getFullYear(), reportingMonth.getMonth(), 0);
        prevEnd.setHours(23, 59, 59, 999);

        const currStart = new Date(reportingMonth.getFullYear(), reportingMonth.getMonth() - f_i + 1, 1);
        currStart.setHours(0, 0, 0, 0);
        const currEnd = new Date(reportingMonth.getFullYear(), reportingMonth.getMonth() + 1, 0);
        currEnd.setHours(23, 59, 59, 999);

        let prev_i = 0;
        let curr_i = 0;

        customer.orders.forEach((order) => {
          if (!order.value || order.value < 50000) return;
          const orderDate = new Date(order.placed_date);
          if (orderDate >= prevStart && orderDate <= prevEnd) {
            prev_i += order.value || 0;
          }
          if (orderDate >= currStart && orderDate <= currEnd) {
            curr_i += order.value || 0;
          }
        });

        customer.prev_i[monthKey] = prev_i;
        customer.curr_i[monthKey] = curr_i;
      });

      // Find onboarded month and reactivation month
      let onboardedMonth = null;
      let hasChurned = false;
      let firstReactivationMonth = null;
      let hadRevenueBefore = false;
      let lastMonthWithRevenue = null;

      allMonths.forEach((monthKey) => {
        const prev_i = customer.prev_i[monthKey];
        const curr_i = customer.curr_i[monthKey];
        const hasRevenueThisMonth = curr_i !== undefined && curr_i !== null && curr_i > 0;

        // Check for onboarding FIRST, before updating hadRevenueBefore
        // Onboarding: prev_i === 0 && curr_i > 0 && no previous revenue
        if (prev_i === 0 && hasRevenueThisMonth && !onboardedMonth && !hadRevenueBefore) {
          onboardedMonth = monthKey;
        }

        // Update hadRevenueBefore after checking for onboarding
        if (prev_i !== undefined && prev_i !== null && prev_i > 0) {
          hadRevenueBefore = true;
        }
        if (hasRevenueThisMonth) {
          hadRevenueBefore = true;
          lastMonthWithRevenue = monthKey;
        }

        if (hadRevenueBefore && !hasRevenueThisMonth && lastMonthWithRevenue !== null) {
          const monthIndex = allMonths.indexOf(monthKey);
          const lastRevenueIndex = allMonths.indexOf(lastMonthWithRevenue);
          if (monthIndex > lastRevenueIndex) {
            hasChurned = true;
          }
        }

        if (!firstReactivationMonth && hasChurned && hasRevenueThisMonth) {
          firstReactivationMonth = monthKey;
        }
      });

      customer.onboardedMonth = onboardedMonth;
      customer.firstReactivationMonth = firstReactivationMonth;
    });

    return customerMap;
  }, [rawData, months]);

  // Define isActiveCustomer after customerLevelMetrics is available
  // Use periodicity-based churn detection (same as Retention/Logos tabs)
  const isActiveCustomer = useMemo(() => {
    return (group) => {
      // Check if this organization is in the active override list
      if (activeOrgIds.includes(group.organization_id)) {
        return true;
      }
      // Check if this organization is in the override list
      if (inactiveOrgIds.includes(group.organization_id)) {
        return false;
      }

      // Use periodicity-based churn detection
      // Check the most recent month's prev_i and curr_i
      const customer = customerLevelMetrics.get(group.organization_id);
      if (!customer) {
        // Fallback to old logic if customer not in metrics
        const now = new Date();
        let hasRecentOrder = false;
        let hasRecentActivity = false;

        group.items
          .filter((item) => item.order_date)
          .forEach((item) => {
            if (item[dateField]) {
              const booked = new Date(item[dateField]);
              const twoMonthsAgo = new Date(now);
              twoMonthsAgo.setMonth(now.getMonth() - 2);
              if (booked >= twoMonthsAgo) {
                hasRecentOrder = true;
              }
            }
            if (item.last_activity_at) {
              const lastActivity = new Date(item.last_activity_at);
              const oneMonthAgo = new Date(now);
              oneMonthAgo.setMonth(now.getMonth() - 1);
              if (lastActivity >= oneMonthAgo) {
                hasRecentActivity = true;
              }
            }
          });
        return hasRecentOrder || hasRecentActivity;
      }

      // Check if customer has churned in ANY month within the date range
      // Once a customer churns, they remain inactive until they reactivate
      if (monthsForMetrics.length === 0) return false;

      // First, check the most recent month - if they have revenue there, they're active
      const mostRecentMonth = monthsForMetrics[monthsForMetrics.length - 1];
      const mostRecentMonthKey = `${new Date(mostRecentMonth).getFullYear()}-${String(new Date(mostRecentMonth).getMonth() + 1).padStart(2, '0')}`;
      const mostRecentCurr_i = customer.curr_i?.[mostRecentMonthKey];

      // If they have revenue in the most recent month, they're active (reactivated or never churned)
      if (mostRecentCurr_i > 0) {
        return true;
      }

      // Check all months for churn (not just the most recent)
      // A customer is churned if they had revenue (prev_i > 0) but no revenue in current period (curr_i === 0)
      // in any month, and they haven't reactivated since then
      let hasChurnedInAnyMonth = false;

      monthsForMetrics.forEach((monthStr) => {
        const monthKey = `${new Date(monthStr).getFullYear()}-${String(new Date(monthStr).getMonth() + 1).padStart(2, '0')}`;
        const prev_i = customer.prev_i?.[monthKey];
        const curr_i = customer.curr_i?.[monthKey];

        // Check if this month shows churn: prev_i > 0 but curr_i === 0
        if (prev_i > 0 && (curr_i === undefined || curr_i === null || curr_i === 0)) {
          hasChurnedInAnyMonth = true;
        }
      });

      // If customer churned in any month and has no revenue in most recent month, they're inactive
      if (hasChurnedInAnyMonth) {
        return false;
      }

      // Additional check: Check last order date from ALL orders (ignoring date filters)
      // This handles cases where prev_i might be 0 due to date range limitations
      let lastOrderDate = null;

      // Get ALL orders for this organization from rawData, ignoring date filters
      if (rawData && rawData.orders) {
        const allOrgOrders = rawData.orders.filter(
          order => order.organization_id === group.organization_id &&
                   order.placed_date &&
                   order.value &&
                   order.value >= 50000
        );

        if (allOrgOrders.length > 0) {
          const orderDates = allOrgOrders
            .map(order => new Date(order.placed_date))
            .filter(date => !isNaN(date.getTime()));
          if (orderDates.length > 0) {
            lastOrderDate = orderDates.sort((a, b) => b - a)[0];
          }
        }
      }

      // Fallback to customer.orders if rawData is not available
      if (!lastOrderDate && customer.orders && customer.orders.length > 0) {
        const orderDates = customer.orders
          .filter(order => order.placed_date)
          .map(order => new Date(order.placed_date))
          .filter(date => !isNaN(date.getTime()));
        if (orderDates.length > 0) {
          lastOrderDate = orderDates.sort((a, b) => b - a)[0];
        }
      }

      if (lastOrderDate) {
        const now = new Date();
        // Calculate months since last order
        const monthsSinceLastOrder = (now.getFullYear() - lastOrderDate.getFullYear()) * 12 +
                                     (now.getMonth() - lastOrderDate.getMonth());

        // If last order was more than 9 months ago, mark as churned
        // Use a threshold of 9 months or 3x the periodicity (whichever is larger)
        const churnThreshold = Math.max(9, customer.demand_periodicity * 3);
        if (monthsSinceLastOrder > churnThreshold) {
          return false; // Inactive/churned
        }
        // If they have a recent order (within threshold), they're active
        return true;
      }

      // If no orders found and no churn detected, they might be new/inactive
      // Default to inactive if no recent revenue and no orders
      return false;
    };
  }, [customerLevelMetrics, monthsForMetrics, dateField, rawData]);

  const sortedGrouped = useMemo(() => {
    const filteredGrouped = grouped.filter((g) => {
      if (filters.size === "all") return true;
      return g.size === filters.size;
    });

    // If no sort key is set, preserve the current order (from rowOrderRef if available)
    if (!sortConfig.key) {
      // If we have a stored order, use it; otherwise use original order
      if (rowOrderRef.current.size > 0) {
        // Sort by stored order
        const ordered = [...filteredGrouped].sort((a, b) => {
          const aOrder = rowOrderRef.current.get(a.organization_id);
          const bOrder = rowOrderRef.current.get(b.organization_id);
          if (aOrder === undefined && bOrder === undefined) return 0;
          if (aOrder === undefined) return 1;
          if (bOrder === undefined) return -1;
          return aOrder - bOrder;
        });
        // Update map with any new items
        ordered.forEach((item, index) => {
          if (!rowOrderRef.current.has(item.organization_id)) {
            rowOrderRef.current.set(item.organization_id, index);
          }
        });
        return ordered;
      }
      // No stored order - use original order and store it
      filteredGrouped.forEach((item, index) => {
        rowOrderRef.current.set(item.organization_id, index);
      });
      return filteredGrouped;
    }

    const sortFn = (a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === "customer") {
        aVal = aVal?.toLowerCase() || "";
        bVal = bVal?.toLowerCase() || "";
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (sortConfig.key === "lastActivity") {
        const aDate = a.lastActivity ? new Date(a.lastActivity) : new Date(0);
        const bDate = b.lastActivity ? new Date(b.lastActivity) : new Date(0);
        return sortConfig.direction === "asc" ? aDate - bDate : bDate - aDate;
      }

      if (sortConfig.key.startsWith("month_")) {
        const month = sortConfig.key.slice(6);
        // Construct monthKey the same way as in customerLevelMetrics
        // month is a string like "Oct 2025" from getMonthYearString
        const reportingMonth = new Date(month);
        const monthKey = `${reportingMonth.getFullYear()}-${String(reportingMonth.getMonth() + 1).padStart(2, '0')}`;

        // Get customer-level metrics for sorting
        const aCustomer = customerLevelMetrics.get(a.organization_id);
        const bCustomer = customerLevelMetrics.get(b.organization_id);

        if (viewMode === "retention") {
          // RR sorting: values (sorted by %) > green (onboarded) > blue (reactivations) > red (churns) > blanks
          // This matches Dashboard -> Retention -> Detail Pane sorting logic exactly
          const aPrev_i = aCustomer?.prev_i?.[monthKey];
          const aCurr_i = aCustomer?.curr_i?.[monthKey];
          const bPrev_i = bCustomer?.prev_i?.[monthKey];
          const bCurr_i = bCustomer?.curr_i?.[monthKey];

          // If customer not in metrics, treat as blank
          if (!aCustomer || !bCustomer) {
            // If one is missing, put missing one last
            if (!aCustomer && bCustomer) return 1;
            if (aCustomer && !bCustomer) return -1;
            if (!aCustomer && !bCustomer) return 0;
          }

          // Calculate RR values
          const aRR = aPrev_i > 0 ? (aCurr_i || 0) / aPrev_i : null;
          const bRR = bPrev_i > 0 ? (bCurr_i || 0) / bPrev_i : null;

          // Check if onboarded month (green)
          // Ensure we compare the monthKey correctly
          const aIsOnboarded = aCustomer && aCustomer.onboardedMonth === monthKey;
          const bIsOnboarded = bCustomer && bCustomer.onboardedMonth === monthKey;

          // Check if reactivation month (blue)
          const aIsReactivation = aCustomer && aCustomer.firstReactivationMonth === monthKey;
          const bIsReactivation = bCustomer && bCustomer.firstReactivationMonth === monthKey;

          // Check if churn (red) - prev_i > 0 and curr_i === 0
          const aIsChurn = aPrev_i > 0 && (aCurr_i === undefined || aCurr_i === null || aCurr_i === 0);
          const bIsChurn = bPrev_i > 0 && (bCurr_i === undefined || bCurr_i === null || bCurr_i === 0);

          // Check if blank (prev_i === 0 or undefined/null, but NOT if onboarded or reactivation)
          // Blank means: no previous revenue AND not onboarded AND not reactivation
          const aIsBlank = (aPrev_i === undefined || aPrev_i === null || aPrev_i === 0) && !aIsOnboarded && !aIsReactivation;
          const bIsBlank = (bPrev_i === undefined || bPrev_i === null || bPrev_i === 0) && !bIsOnboarded && !bIsReactivation;

          // Priority order: values (sorted) > green (onboarded) > blue (reactivations) > red (churns) > blanks
          // Use priority-based sorting to ensure correct order

          // Values: positive RR (not onboarded, not reactivation, not churn, not blank, RR > 0)
          const aIsValue = !aIsOnboarded && !aIsReactivation && !aIsChurn && !aIsBlank && aRR !== null && aRR > 0;
          const bIsValue = !bIsOnboarded && !bIsReactivation && !bIsChurn && !bIsBlank && bRR !== null && bRR > 0;

          // Assign priority: 1=value, 2=green, 3=blue, 4=red, 5=blank
          const getPriority = (isValue, isOnboarded, isReactivation, isChurn, isBlank) => {
            if (isValue) return 1;
            if (isOnboarded) return 2;
            if (isReactivation) return 3;
            if (isChurn) return 4;
            if (isBlank) return 5;
            return 5; // Default to blank priority
          };

          const aPriority = getPriority(aIsValue, aIsOnboarded, aIsReactivation, aIsChurn, aIsBlank);
          const bPriority = getPriority(bIsValue, bIsOnboarded, bIsReactivation, bIsChurn, bIsBlank);

          // If different priorities, sort by priority
          if (aPriority !== bPriority) {
            return aPriority < bPriority ? -1 : 1;
          }

          // If same priority, handle special cases
          if (aIsValue && bIsValue) {
            // Both are values, sort by RR value
            if (aRR < bRR) return sortConfig.direction === "asc" ? -1 : 1;
            if (aRR > bRR) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
          }

          // For other priorities (green, blue, red, blank), maintain order
          return 0;
        } else if (viewMode === "logos") {
          // LR/LG sorting: use standard value sorting
          const aPrev_i = aCustomer?.prev_i?.[monthKey];
          const aCurr_i = aCustomer?.curr_i?.[monthKey];
          const bPrev_i = bCustomer?.prev_i?.[monthKey];
          const bCurr_i = bCustomer?.curr_i?.[monthKey];

          // For LR: show if prev_i > 0 (retained or churned)
          // For LG: show if prev_i === 0 && curr_i > 0 (new logo)
          // Use curr_i as the value for sorting
          let aVal = aCurr_i || 0;
          let bVal = bCurr_i || 0;

          // Handle null/undefined values
          if (aVal === null || aVal === undefined) aVal = -Infinity;
          if (bVal === null || bVal === undefined) bVal = -Infinity;

          if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
          if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
          return 0;
        } else {
          // Revenue view: use revenue totals
          const aTotal = a.items
            .filter(
              (i) =>
                i[dateField] &&
                getMonthYearString(new Date(i[dateField])) === month
            )
            .reduce((sum, i) => sum + (i.value || 0), 0);
          const bTotal = b.items
            .filter(
              (i) =>
                i[dateField] &&
                getMonthYearString(new Date(i[dateField])) === month
            )
            .reduce((sum, i) => sum + (i.value || 0), 0);
          return sortConfig.direction === "asc"
            ? aTotal - bTotal
            : bTotal - aTotal;
        }
      }

      if (sortConfig.key === "currentCartValue" || sortConfig.key === "nextCartValue") {
        const aCart = a.items
          .filter((i) => i.order_date && !i[dateField])
          .reduce((sum, i) => sum + (i.value || 0), 0);
        const bCart = b.items
          .filter((i) => i.order_date && !i[dateField])
          .reduce((sum, i) => sum + (i.value || 0), 0);
        return sortConfig.direction === "asc" ? aCart - bCart : bCart - aCart;
      }

      if (sortConfig.key === "totalOrderValue") {
        const aTotal = a.items
          .filter((i) => i.order_date && i[dateField])
          .reduce((sum, i) => sum + (i.value || 0), 0);
        const bTotal = b.items
          .filter((i) => i.order_date && i[dateField])
          .reduce((sum, i) => sum + (i.value || 0), 0);
        return sortConfig.direction === "asc"
          ? aTotal - bTotal
          : bTotal - aTotal;
      }

      if (sortConfig.key === "amov") {
        const aVal = computeAmovValue(a);
        const bVal = computeAmovValue(b);

        if (aVal == null) return 1;
        if (bVal == null) return -1;

        return sortConfig.direction === "asc"
          ? aVal.amov - bVal.amov
          : bVal.amov - aVal.amov;
      }

      return 0;
    };

    // Sort all together
    const sorted = filteredGrouped.sort(sortFn);

    // Update row order ref with the new sorted order to preserve across view switches
    sorted.forEach((item, index) => {
      rowOrderRef.current.set(item.organization_id, index);
    });

    return sorted;
  }, [grouped, sortConfig, filters, dateField, viewMode, customerLevelMetrics]);

  const totalAmov = useMemo(() => {
    return sortedGrouped
      .filter((group) => isActiveCustomer(group))
      .reduce((sum, group) => {
        const amovValue = computeAmovValue(group);
        return sum + (amovValue?.amov || 0);
      }, 0);
  }, [sortedGrouped, dateField]);

  const totals = useMemo(() => {
    return months.map((month) =>
      effectiveProjectOrders
        .filter(
          (p) =>
            p.organization_status === "customer" &&
            p[dateField] &&
            getMonthYearString(new Date(p[dateField])) === month
        )
        .reduce((sum, p) => sum + (p.value || 0), 0)
    );
  }, [effectiveProjectOrders, months, dateField]);

  const totalCurrentMonthCart = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    return effectiveProjectOrders
      .filter((p) => {
        if (
          !p.order_date ||
          p.organization_status !== "customer" ||
          p[dateField]
        ) {
          return false;
        }

        const date = new Date(p.order_date);
        return (
          date.getMonth() === currentMonth && date.getFullYear() === currentYear
        );
      })
      .reduce((sum, p) => sum + (p.value || 0), 0);
  }, [effectiveProjectOrders, dateField]);

  const totalNextMonthCart = useMemo(() => {
    const now = new Date();
    const nextMonthDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const nextMonth = nextMonthDate.getMonth();
    const nextYear = nextMonthDate.getFullYear();

    return effectiveProjectOrders
      .filter((p) => {
        if (
          !p.order_date ||
          p.organization_status !== "customer" ||
          p[dateField]
        ) {
          return false;
        }

        const date = new Date(p.order_date);
        return date.getMonth() === nextMonth && date.getFullYear() === nextYear;
      })
      .reduce((sum, p) => sum + (p.value || 0), 0);
  }, [effectiveProjectOrders, dateField]);

  const firstOrderTotals = useMemo(() => {
    const map = {};

    grouped.forEach((group) => {
      const booked = group.items.filter((i) => i[dateField] && i.value && i.value >= 50000);
      if (booked.length === 0) return;

      const firstPlaced = new Date(
        Math.min(...booked.map((i) => new Date(i[dateField])))
      );
      const monthKey = getMonthYearString(firstPlaced);
      const totalValue = booked
        .filter((i) => getMonthYearString(new Date(i[dateField])) === monthKey)
        .reduce((sum, i) => sum + (i.value || 0), 0);

      map[monthKey] = (map[monthKey] || 0) + totalValue;
    });

    return months.map((month) => map[month] || 0);
  }, [grouped, months, dateField]);

  // Calculate RR, LR, LG metrics for each month
  const monthlyMetrics = useMemo(() => {
    if (!rawData || !rawData.organizations || !rawData.orders) return {};

    const { organizations, orders } = rawData;
    const metrics = {};

    // Only calculate metrics for months excluding current month
    monthsForMetrics.forEach((monthStr) => {
      const reportingMonth = new Date(monthStr);
      const monthKey = `${reportingMonth.getFullYear()}-${String(reportingMonth.getMonth() + 1).padStart(2, '0')}`;

      // Get all customers with their orders
      const customerMap = new Map();
      orders.forEach((order) => {
        if (!order.placed_date || !order.value || order.value < 50000) return;
        const org = organizations[order.organization_id?.toString()];
        if (!org || !org.demand_periodicity) return;

        const orgId = order.organization_id;
        if (!customerMap.has(orgId)) {
          customerMap.set(orgId, {
            organization_id: orgId,
            demand_periodicity: org.demand_periodicity,
            orders: [],
          });
        }
        customerMap.get(orgId).orders.push({
          placed_date: order.placed_date,
          value: order.value,
        });
      });

      const customers = Array.from(customerMap.values());

      // Calculate prev_i and curr_i for each customer
      const T = reportingMonth;
      const customerMetrics = customers.map((customer) => {
        const f_i = customer.demand_periodicity;

        const prevStart = new Date(T.getFullYear(), T.getMonth() - f_i, 1);
        prevStart.setHours(0, 0, 0, 0);
        const prevEnd = new Date(T.getFullYear(), T.getMonth(), 0);
        prevEnd.setHours(23, 59, 59, 999);

        const currStart = new Date(T.getFullYear(), T.getMonth() - f_i + 1, 1);
        currStart.setHours(0, 0, 0, 0);
        const currEnd = new Date(T.getFullYear(), T.getMonth() + 1, 0);
        currEnd.setHours(23, 59, 59, 999);

        let prev_i = 0;
        let curr_i = 0;
        let hadRevenueBeforePrevWindow = false; // Track if customer had revenue before prev_i window

        customer.orders.forEach((order) => {
          if (!order.value || order.value < 50000) return;
          const orderDate = new Date(order.placed_date);

          // Check if order is before prev_i window (indicates reactivation, not new logo)
          if (orderDate < prevStart) {
            hadRevenueBeforePrevWindow = true;
          }

          if (orderDate >= prevStart && orderDate <= prevEnd) {
            prev_i += order.value || 0;
          }
          if (orderDate >= currStart && orderDate <= currEnd) {
            curr_i += order.value || 0;
          }
        });

        return {
          prev_i,
          curr_i,
          organization_id: customer.organization_id,
          isReactivation: hadRevenueBeforePrevWindow && prev_i === 0 && curr_i > 0 // Reactivated if had revenue before but prev_i = 0
        };
      });

      const activeInPrevWindow = customerMetrics.filter((cm) => cm.prev_i > 0);

      if (activeInPrevWindow.length > 0) {
        const sumCurrForActive = customerMetrics
          .filter((cm) => cm.prev_i > 0)
          .reduce((sum, cm) => sum + cm.curr_i, 0);
        const sumPrevForActive = activeInPrevWindow.reduce(
          (sum, cm) => sum + cm.prev_i,
          0
        );
        metrics[monthKey] = {
          revenueRetention: sumPrevForActive > 0 ? sumCurrForActive / sumPrevForActive : null,
          logoRetention: activeInPrevWindow.length > 0
            ? customerMetrics.filter((cm) => cm.prev_i > 0 && cm.curr_i > 0).length / activeInPrevWindow.length
            : null,
          logoGrowth: activeInPrevWindow.length > 0
            ? customerMetrics.filter((cm) => cm.prev_i === 0 && cm.curr_i > 0 && !cm.isReactivation).length / activeInPrevWindow.length
            : null,
          // Store numerator and denominator for RR tooltip
          rrNumerator: sumCurrForActive,
          rrDenominator: sumPrevForActive,
        };
      } else {
        metrics[monthKey] = {
          revenueRetention: null,
          logoRetention: null,
          logoGrowth: null,
          rrNumerator: 0,
          rrDenominator: 0,
        };
      }
    });

    return metrics;
  }, [rawData, monthsForMetrics]);

  const handleSort = (key) => {
    const isAsc = sortConfig.key === key && sortConfig.direction === "asc";
    setSortConfig({ key, direction: isAsc ? "desc" : "asc" });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return "⇅";
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  const newAndChurnCounts = useMemo(() => {
    const monthToNew = {};
    const monthToChurned = {};

    grouped.forEach((group) => {
      const placed = group.items.filter((i) => i[dateField] && i.value && i.value >= 50000);

      if (placed.length > 0) {
        const firstPlaced = placed
          .map((i) => new Date(i[dateField]))
          .sort((a, b) => a - b)[0];
        const firstMonth = getMonthYearString(firstPlaced);
        monthToNew[firstMonth] = (monthToNew[firstMonth] || 0) + 1;

        const lastPlaced = placed
          .map((i) => new Date(i[dateField]))
          .sort((a, b) => b - a)[0];
        const churnCutoff = new Date();
        churnCutoff.setMonth(churnCutoff.getMonth() - 3);
        if (lastPlaced < churnCutoff) {
          const lastMonth = getMonthYearString(lastPlaced);
          monthToChurned[lastMonth] = (monthToChurned[lastMonth] || 0) + 1;
        }
      }
    });

    return months.map((month) => ({
      newCount: monthToNew[month] || 0,
      churnedCount: monthToChurned[month] || 0,
    }));
  }, [grouped, months, dateField]);

  return (
    <Row className="border p-3 bg-white rounded">
      <Col className="d-flex gap-2 align-items-center mb-2 flex-wrap justify-content-between">
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <ButtonGroup size="sm">
            <Button
              color={dateField === "booked_date" ? "primary" : "outline-primary"}
              onClick={() => setDateField("booked_date")}
            >
              Booked Date
            </Button>
            <Button
              color={dateField === "placed_date" ? "primary" : "outline-primary"}
              onClick={() => setDateField("placed_date")}
            >
              Placed Date
            </Button>
          </ButtonGroup>
        <Input
          type="month"
          value={startDate.slice(0, 7)}
          onChange={(e) => setStartDate(e.target.value + "-01")}
          className="w-auto"
        />
        <span>to</span>
        <Input
          type="month"
          value={endDate.slice(0, 7)}
          onChange={(e) => setEndDate(e.target.value + "-01")}
          className="w-auto"
        />
          <label className="form-label mb-0 ms-4 me-1 fw-bold">Size:</label>
          <select
            className="form-select form-select-sm w-auto me-3"
            value={filters.size}
            onChange={(e) => setFilters((f) => ({ ...f, size: e.target.value }))}
          >
            <option value="all">All</option>
            <option value="s">S</option>
            <option value="m">M</option>
            <option value="l">L</option>
            <option value="xl">XL</option>
            <option value="xxl">XXL</option>
          </select>
          <ButtonGroup size="sm" className="ms-3">
            <Button
              color={viewMode === "revenue" ? "primary" : "outline-primary"}
              onClick={() => setViewMode("revenue")}
            >
              Revenue
            </Button>
            <Button
              color={viewMode === "retention" ? "primary" : "outline-primary"}
              onClick={() => setViewMode("retention")}
            >
              Retention
            </Button>
            <Button
              color={viewMode === "logos" ? "primary" : "outline-primary"}
              onClick={() => setViewMode("logos")}
            >
              Logos
            </Button>
          </ButtonGroup>
        </div>
        <ButtonGroup>
          <Button
            color={projectionFilter === "all" ? "primary" : "secondary"}
            onClick={() => setProjectionFilter("all")}
          >
            All
          </Button>
          <Button
            color={projectionFilter === "placed" ? "primary" : "secondary"}
            onClick={() => setProjectionFilter("placed")}
          >
            Placed
          </Button>
          <Button
            color={projectionFilter === "projections" ? "primary" : "secondary"}
            onClick={() => setProjectionFilter("projections")}
          >
            Projections
          </Button>
        </ButtonGroup>
      </Col>
      <Table
        bordered
        responsive
        className="text-center align-middle"
        style={{ fontSize: "0.9rem", tableLayout: "fixed" }}
      >
        <thead>
          <tr>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("customer")}
              style={{ width: "200px", minWidth: "200px", maxWidth: "200px" }}
            >
              <div className="d-flex flex-column align-items-center">
                <div>Customer {renderSortIcon("customer")}</div>
                {(() => {
                  const activeCount =
                    sortedGrouped.filter(isActiveCustomer).length;
                  const inactiveCount = sortedGrouped.length - activeCount;
                  return (
                    <div className="fw-normal fs-6">
                      Active: {activeCount} / Inactive: {inactiveCount}
                    </div>
                  );
                })()}
              </div>
            </th>
            <th className="bg-secondary text-white">Size</th>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("amov")}
            >
              AMOV {renderSortIcon("amov")}
            </th>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("totalOrderValue")}
            >
              Total {renderSortIcon("totalOrderValue")}
            </th>
            {(viewMode === "retention" || viewMode === "logos" ? monthsForMetrics : months).map((month, idx) => {
              // Find the index in the original months array for newAndChurnCounts
              const originalIdx = months.indexOf(month);
              const newCount = originalIdx >= 0 ? newAndChurnCounts[originalIdx].newCount : 0;
              const churnedCount = originalIdx >= 0 ? newAndChurnCounts[originalIdx].churnedCount : 0;

              return (
                <th
                  key={idx}
                  className="bg-secondary text-white"
                  role="button"
                  onClick={() => handleSort(`month_${month}`)}
                  style={{ width: "100px", minWidth: "100px", maxWidth: "100px" }}
                >
                  <div className="d-flex flex-column align-items-center">
                    <div style={{ fontSize: "0.9em" }}>
                      {getMonthYearDisplay(new Date(month))} {renderSortIcon(`month_${month}`)}
                    </div>
                    {viewMode === "revenue" && (
                      <div className="fw-normal fs-6" style={{ opacity: 0.8 }}>
                        +{newCount} / -{churnedCount}
                      </div>
                    )}
                  </div>
                </th>
              );
            })}
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("currentCartValue")}
            >
              In Cart {renderSortIcon("currentCartValue")}
            </th>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("nextCartValue")}
            >
              Next Cart {renderSortIcon("nextCartValue")}
            </th>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("lastActivity")}
            >
              Activity {renderSortIcon("lastActivity")}
            </th>
          </tr>
          {/* Total/Metrics row - always present, content changes based on viewMode */}
          <tr className="text-dark">
            {viewMode === "revenue" ? (
              <>
                <td className="bg-EEEEEE fw-bold">Revenue</td>
                <td className="bg-EEEEEE fw-bold"></td>
                <td className="bg-EEEEEE fw-bold">{formatCurrency(totalAmov)}</td>
                <td className="bg-EEEEEE fw-bold">
                  {(() => {
                    const totalPlaced = sortedGrouped.reduce(
                      (sum, group) =>
                        sum +
                        group.items
                          .filter((i) => i.order_date && i[dateField])
                          .reduce((s, i) => s + (i.value || 0), 0),
                      0
                    );
                    return totalPlaced > 0 ? formatCurrency(totalPlaced) : "-";
                  })()}
                </td>
                {totals.map((val, idx) => (
                  <td key={idx} className="bg-EEEEEE fw-bold" style={{ width: "100px", minWidth: "100px", maxWidth: "100px" }}>
                    {val > 0 ? formatCurrency(val) : "-"}
                    {val > 0 && (
                      <div className="text-success small fw-semibold">
                        {formatCurrency(firstOrderTotals[idx])}
                      </div>
                    )}
                  </td>
                ))}
                <td className="bg-EEEEEE fw-bold">
                  {totalCurrentMonthCart > 0
                    ? formatCurrency(totalCurrentMonthCart)
                    : "-"}
                </td>
                <td className="bg-EEEEEE fw-bold">
                  {totalNextMonthCart > 0
                    ? formatCurrency(totalNextMonthCart)
                    : "-"}
                </td>
                <td className="bg-EEEEEE"></td>
              </>
            ) : viewMode === "retention" ? (
              <>
                <td className="bg-EEEEEE fw-bold">Revenue Retention</td>
                <td className="bg-EEEEEE fw-bold"></td>
                <td className="bg-EEEEEE fw-bold"></td>
                <td className="bg-EEEEEE fw-bold"></td>
                {monthsForMetrics.map((month, idx) => {
                  const monthKey = `${new Date(month).getFullYear()}-${String(new Date(month).getMonth() + 1).padStart(2, '0')}`;
                  const rr = monthlyMetrics[monthKey]?.revenueRetention;
                  const prevMonthKey = idx > 0 ? `${new Date(monthsForMetrics[idx - 1]).getFullYear()}-${String(new Date(monthsForMetrics[idx - 1]).getMonth() + 1).padStart(2, '0')}` : null;
                  const prevRR = prevMonthKey ? monthlyMetrics[prevMonthKey]?.revenueRetention : null;
                  const trend = prevRR !== null && rr !== null ? (rr > prevRR ? "↑" : rr < prevRR ? "↓" : "−") : "";
                  const rrNumerator = monthlyMetrics[monthKey]?.rrNumerator || 0;
                  const rrDenominator = monthlyMetrics[monthKey]?.rrDenominator || 0;
                  const tooltipId = `rr-header-tooltip-${monthKey}`;

                  return (
                    <td
                      key={idx}
                      id={tooltipId}
                      className="bg-EEEEEE fw-bold text-center"
                      style={{
                        width: "100px",
                        minWidth: "100px",
                        maxWidth: "100px"
                      }}
                    >
                      {rr !== null && rr !== undefined ? (
                        <>
                          <span style={{ cursor: "help" }}>
                            {(rr * 100).toFixed(0)}% {trend}
                          </span>
                          <UncontrolledTooltip target={tooltipId} placement="top">
                            <div style={{ textAlign: "left" }}>
                              <div><strong>Revenue Retention (RR)</strong></div>
                              <div style={{ marginTop: "8px" }}>
                                <div><strong>Current window (sum):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(rrNumerator)}</div>
                                <div><strong>Previous window (sum):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(rrDenominator)}</div>
                                <div style={{ marginTop: "4px", fontSize: "0.85rem" }}>
                                  RR = {rrDenominator > 0 ? `${(rr * 100).toFixed(1)}%` : "N/A"}
                                </div>
                                <div style={{ fontSize: "0.75rem", marginTop: "4px", color: "#666" }}>
                                  Note: Windows overlap by 1 month
                                </div>
                              </div>
                            </div>
                          </UncontrolledTooltip>
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                  );
                })}
                <td className="bg-EEEEEE"></td>
                <td className="bg-EEEEEE"></td>
                <td className="bg-EEEEEE"></td>
              </>
            ) : (
              <>
                <td className="bg-EEEEEE fw-bold">Logos</td>
                <td className="bg-EEEEEE fw-bold"></td>
                <td className="bg-EEEEEE fw-bold"></td>
                <td className="bg-EEEEEE fw-bold"></td>
                {monthsForMetrics.map((month, idx) => {
                  const monthKey = `${new Date(month).getFullYear()}-${String(new Date(month).getMonth() + 1).padStart(2, '0')}`;
                  const lr = monthlyMetrics[monthKey]?.logoRetention;
                  const lg = monthlyMetrics[monthKey]?.logoGrowth;
                  const prevMonthKey = idx > 0 ? `${new Date(monthsForMetrics[idx - 1]).getFullYear()}-${String(new Date(monthsForMetrics[idx - 1]).getMonth() + 1).padStart(2, '0')}` : null;
                  const prevLR = prevMonthKey ? monthlyMetrics[prevMonthKey]?.logoRetention : null;
                  const prevLG = prevMonthKey ? monthlyMetrics[prevMonthKey]?.logoGrowth : null;
                  const lrTrend = prevLR !== null && lr !== null ? (lr > prevLR ? "↑" : lr < prevLR ? "↓" : "−") : "";
                  const lgTrend = prevLG !== null && lg !== null ? (lg > prevLG ? "↑" : lg < prevLG ? "↓" : "−") : "";

                  return (
                    <td key={idx} className="bg-EEEEEE fw-bold text-center" style={{ width: "100px", minWidth: "100px", maxWidth: "100px" }}>
                      <div>
                        {lr !== null && lr !== undefined ? (
                          <>{(lr * 100).toFixed(0)}% {lrTrend}</>
                        ) : (
                          "-"
                        )}
                      </div>
                      <div>
                        {lg !== null && lg !== undefined ? (
                          <>{(lg * 100).toFixed(0)}% {lgTrend}</>
                        ) : (
                          "-"
                        )}
                      </div>
                    </td>
                  );
                })}
                <td className="bg-EEEEEE"></td>
                <td className="bg-EEEEEE"></td>
                <td className="bg-EEEEEE"></td>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {sortedGrouped.map((group, idx) => {
            const placed = group.items.filter(
              (i) => i.order_date && i[dateField] && i.value && i.value >= 50000
            );
            const cart = group.items.filter(
              (i) => i.order_date && !i[dateField]
            );

            const now = new Date();
            const currentMonth = now.getMonth();
            const currentYear = now.getFullYear();
            const nextMonthDate = new Date(currentYear, currentMonth + 1, 1);
            const nextMonth = nextMonthDate.getMonth();
            const nextYear = nextMonthDate.getFullYear();

            const result = {
              current: [],
              next: [],
            };

            cart.forEach((item) => {
              const date = new Date(item.order_date);
              const month = date.getMonth();
              const year = date.getFullYear();

              if (month === currentMonth && year === currentYear) {
                result.current.push(item);
              } else if (month === nextMonth && year === nextYear) {
                result.next.push(item);
              }
            });

            console.log(result);

            const currentCartValue = result.current.reduce(
              (sum, i) => sum + (i.value || 0),
              0
            );
            const currentCartCount = result.current.length;

            const nextCartValue = result.next.reduce(
              (sum, i) => sum + (i.value || 0),
              0
            );
            const nextCartCount = result.next.length;

            const firstPlaced =
              placed.length > 0
                ? new Date(
                    Math.min(...placed.map((i) => new Date(i[dateField])))
                  )
                : null;

            const lastPlaced =
              placed.length > 0
                ? new Date(
                    Math.max(...placed.map((i) => new Date(i[dateField])))
                  )
                : null;
            const hasChurnOrder = !isActiveCustomer(group);
            const amovResult = computeAmovValue(group);
            const isRepeat = amovResult != null;

            return (
              <tr
                key={idx}
                className={idx % 2 === 0 ? "table-light" : "table-white"}
                onClick={() => setSelectedOrgId(group.organization_id)}
                style={{ cursor: "pointer" }}
              >
                <td
                  className={`text-start fw-semibold ${
                    !isActiveCustomer(group) && "text-danger"
                  }`}
                >
                  {group.customer}
                  <span className="grey_letter fw-normal">
                    #{group.organization_id}
                  </span>
                  {viewMode === "retention" && rawData?.organizations?.[group.organization_id?.toString()]?.demand_periodicity && (
                    <span className="text-muted fw-normal ms-2" style={{ fontSize: "0.85em" }}>
                      ({rawData.organizations[group.organization_id.toString()].demand_periodicity}m)
                    </span>
                  )}
                </td>
                <td className="text-center small px-3 py-2">
                  {group.size?.toUpperCase?.() || "-"}
                </td>
                <td>
                  {isActiveCustomer(group) && amovResult && (
                    <div className="d-inline-flex align-items-center justify-content-center gap-1 text-primary fw-bold">
                      {formatCurrency(amovResult.amov)}
                    </div>
                  )}
                </td>
                <td className="text-end text-success fw-bold">
                  {placed.length > 0 ? (
                    <div className="d-inline-flex align-items-center justify-content-center gap-1">
                      {placed.length > 1 && (
                        <Badge
                          color="light"
                          pill
                          className="border text-muted small px-2"
                        >
                          {placed.length}
                        </Badge>
                      )}
                      <span>
                        {formatCurrency(
                          placed.reduce((sum, i) => sum + (i.value || 0), 0)
                        )}
                      </span>
                    </div>
                  ) : (
                    ""
                  )}
                </td>
                {(viewMode === "retention" || viewMode === "logos" ? monthsForMetrics : months).map((monthLabel, colIdx) => {
                  const monthKey = `${new Date(monthLabel).getFullYear()}-${String(new Date(monthLabel).getMonth() + 1).padStart(2, '0')}`;
                  const customer = customerLevelMetrics.get(group.organization_id);

                  // Get customer-level metrics for this month
                  const prev_i = customer?.prev_i?.[monthKey];
                  const curr_i = customer?.curr_i?.[monthKey];
                  const isReactivationMonth = customer?.firstReactivationMonth === monthKey;
                  const isOnboardedMonth = customer?.onboardedMonth === monthKey;

                  const monthOrders = group.items.filter(
                    (o) =>
                      o[dateField] &&
                      getMonthYearString(new Date(o[dateField])) === monthLabel
                  );
                  const totalValue = monthOrders.reduce(
                    (sum, o) => sum + (o.value || 0),
                    0
                  );
                  const count = monthOrders.length;

                  const monthDateObj = new Date(monthLabel);

                  const beforeFirstOrder =
                    firstPlaced &&
                    monthDateObj <
                      new Date(
                        firstPlaced.getFullYear(),
                        firstPlaced.getMonth(),
                        1
                      );

                  const afterChurn =
                    !isActiveCustomer(group) &&
                    lastPlaced &&
                    monthDateObj >
                      new Date(
                        lastPlaced.getFullYear(),
                        lastPlaced.getMonth(),
                        1
                      );

                  const monthFirstPlaced = firstPlaced
                    ? getMonthYearString(firstPlaced)
                    : null;
                  const monthLastPlaced = lastPlaced
                    ? getMonthYearString(lastPlaced)
                    : null;

                  const isFirstOrderMonth = monthFirstPlaced === monthLabel;

                  // Use periodicity-based churn detection (consistent across all tabs)
                  const isChurnedBasedOnPeriodicity = prev_i > 0 && (curr_i === undefined || curr_i === null || curr_i === 0);

                  const isSimultaneousNewChurn =
                    isFirstOrderMonth && isChurnedBasedOnPeriodicity;

                  // Determine background color based on status (consistent across all tabs)
                  let cellStyle = {};
                  if (isReactivationMonth) {
                    cellStyle = { backgroundColor: "#cfe2ff" }; // Blue for reactivation
                  } else if (isOnboardedMonth || isFirstOrderMonth) {
                    cellStyle = { backgroundColor: "#d1e7dd" }; // Green for onboarded/first order
                  } else if (isChurnedBasedOnPeriodicity) {
                    cellStyle = { backgroundColor: "#f8d7da" }; // Red for churn
                  }
                  // Note: Regular orders (not onboarded/churned/reactivated) will have no special background
                  // They'll get light gray in the Revenue view below if they have orders

                  const fixedWidthStyle = { width: "100px", minWidth: "100px", maxWidth: "100px" };

                  if (beforeFirstOrder || afterChurn) {
                    return <td key={colIdx} style={{ ...cellStyle, ...fixedWidthStyle }}></td>;
                  }

                  // Render based on viewMode
                  if (viewMode === "revenue") {
                    // Revenue view: show revenue values with consistent background colors
                    if (count === 0 || totalValue === 0) {
                      return (
                        <td
                          key={colIdx}
                          style={{ ...cellStyle, ...fixedWidthStyle }}
                          className={`${hasChurnOrder && "bg-EEEEEE"}`}
                        ></td>
                      );
                    }

                    // Use consistent subtle background colors (same as Retention/Logos tabs)
                    // cellStyle already has the correct background color set above based on status
                    let revenueBgColor = cellStyle;
                    if (!revenueBgColor.backgroundColor) {
                      // Default to light gray for regular orders (presence of order indicator)
                      if (isSimultaneousNewChurn) {
                        revenueBgColor = { backgroundColor: "#fff3cd" }; // Yellow/warning (subtle)
                      } else {
                        revenueBgColor = { backgroundColor: "#e9ecef" }; // Light gray for regular orders
                      }
                    }

                    return (
                      <td
                        key={colIdx}
                        style={{ ...revenueBgColor, ...fixedWidthStyle, cursor: "pointer" }}
                        className="text-end"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMonthData({
                            customer: group.customer,
                            month: monthLabel,
                            orders: monthOrders,
                          });
                        }}
                      >
                        <div className="d-inline-flex align-items-center justify-content-center gap-1">
                          {count > 1 && (
                            <Badge
                              color="light"
                              pill
                              className="border text-muted small px-2"
                            >
                              {count}
                            </Badge>
                          )}
                          <span>{formatCurrency(totalValue)}</span>
                        </div>
                      </td>
                    );
                  } else if (viewMode === "retention") {
                    // Retention view: show RR values with background colors
                    const tooltipId = `rr-tooltip-${group.organization_id}-${monthKey}`;
                    let bgColor = {};

                    // Check for blank cells first
                    // A cell is blank only if:
                    // 1. Customer not in metrics (customer is undefined)
                    // 2. OR prev_i is undefined/null AND it's not onboarded or reactivated
                    // 3. OR prev_i === 0 AND curr_i === 0 AND it's not onboarded or reactivated AND no orders in prev window
                    // BUT: If prev_i > 0, we should always show something (even if curr_i is 0, that's a churn)

                    if (!customer) {
                      // Customer not in metrics - show blank
                      return <td key={colIdx} className="text-center" style={{ ...cellStyle, ...fixedWidthStyle }}></td>;
                    }

                    // If prev_i is undefined/null, only show blank if not onboarded/reactivated
                    if ((prev_i === undefined || prev_i === null) && !isOnboardedMonth && !isReactivationMonth) {
                      return <td key={colIdx} className="text-center" style={{ ...cellStyle, ...fixedWidthStyle }}></td>;
                    }

                    // If prev_i === 0, check if it's truly blank (no orders in previous window)
                    // Only show blank if: prev_i === 0 AND curr_i === 0 AND not onboarded/reactivated
                    if (prev_i === 0 && (curr_i === undefined || curr_i === null || curr_i === 0) && !isOnboardedMonth && !isReactivationMonth) {
                      // Check if there are any orders (>= 50000) in the previous window period
                      const reportingMonth = new Date(monthLabel);
                      const f_i = customer.demand_periodicity || 2;
                      const prevStart = new Date(reportingMonth.getFullYear(), reportingMonth.getMonth() - f_i, 1);
                      prevStart.setHours(0, 0, 0, 0);
                      const prevEnd = new Date(reportingMonth.getFullYear(), reportingMonth.getMonth(), 0);
                      prevEnd.setHours(23, 59, 59, 999);

                      const hasOrdersInPrevWindow = customer.orders && customer.orders.some(order => {
                        if (!order.value || order.value < 50000) return false;
                        const orderDate = new Date(order.placed_date);
                        return orderDate >= prevStart && orderDate <= prevEnd;
                      });

                      if (!hasOrdersInPrevWindow) {
                        // Truly blank - no orders in previous window
                        return <td key={colIdx} className="text-center" style={{ ...cellStyle, ...fixedWidthStyle }}></td>;
                      }
                      // If has orders in prev window but prev_i is 0, it might be a calculation issue
                      // Continue to show RR (will show as churn if curr_i is also 0)
                    }

                    // If prev_i > 0, we should always show something (RR value or churn)
                    // Don't show blank if prev_i > 0, even if curr_i is 0 (that's a churn, not blank)

                    if (isOnboardedMonth) {
                      // Green for onboarded
                      bgColor = { backgroundColor: "#d1e7dd" };
                      return (
                        <td
                          key={colIdx}
                          id={tooltipId}
                          className="text-center"
                          style={{ ...bgColor, ...fixedWidthStyle, cursor: "help" }}
                        >
                          <UncontrolledTooltip target={tooltipId} placement="top">
                            <div style={{ textAlign: "left" }}>
                              <div><strong>Onboarded</strong></div>
                              <div><strong>Current window ({customer?.demand_periodicity || 2}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(curr_i || 0)}</div>
                            </div>
                          </UncontrolledTooltip>
                        </td>
                      );
                    }

                    if (isReactivationMonth) {
                      // Blue for reactivation
                      bgColor = { backgroundColor: "#cfe2ff" };
                      const rrValue = prev_i > 0 ? (curr_i || 0) / prev_i : null;
                      return (
                        <td
                          key={colIdx}
                          id={tooltipId}
                          className="text-center"
                          style={{ ...bgColor, ...fixedWidthStyle, cursor: "help" }}
                        >
                          <UncontrolledTooltip target={tooltipId} placement="top">
                            <div style={{ textAlign: "left" }}>
                              <div><strong>Reactivation</strong></div>
                              <div style={{ marginTop: "8px" }}>
                                <div><strong>Current window ({customer?.demand_periodicity || 2}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(curr_i || 0)}</div>
                                {prev_i > 0 && (
                                  <>
                                    <div><strong>Previous window ({customer?.demand_periodicity || 2}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(prev_i)}</div>
                                    {rrValue !== null && (
                                      <div style={{ marginTop: "4px", fontSize: "0.85rem" }}>
                                        RR = {(rrValue * 100).toFixed(1)}%
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </UncontrolledTooltip>
                        </td>
                      );
                    }

                    // Don't show blank if prev_i is 0 but customer had revenue before (they're churned, not blank)
                    // Only show blank if prev_i is undefined/null (not calculated) or if truly no revenue ever
                    if (prev_i === undefined || prev_i === null) {
                      // Not calculated - show blank unless it's a special case
                      return <td key={colIdx} className="text-center" style={{ ...cellStyle, ...fixedWidthStyle }}></td>;
                    }

                    // If prev_i === 0, check if customer had revenue before (churned) or never had revenue (blank)
                    if (prev_i === 0) {
                      // Check if customer had any revenue before this month
                      const hadRevenueBefore = customer.orders && customer.orders.some(order => {
                        if (!order.value || order.value < 50000) return false;
                        const orderDate = new Date(order.placed_date);
                        const reportingMonth = new Date(monthLabel);
                        return orderDate < reportingMonth; // Any order before this reporting month
                      });

                      // If no revenue before and curr_i is also 0, show blank
                      if (!hadRevenueBefore && (curr_i === undefined || curr_i === null || curr_i === 0)) {
                        return <td key={colIdx} className="text-center" style={{ ...cellStyle, ...fixedWidthStyle }}></td>;
                      }
                      // Otherwise, continue to show RR (will show as churn if curr_i is 0)
                    }

                    // Calculate RR value
                    // If prev_i > 0, calculate normally
                    // If prev_i === 0 but customer had revenue before, it's a churn (RR = 0%)
                    // If prev_i === 0 and no revenue before, it should have been handled as blank above
                    const rrValue = prev_i > 0 ? (curr_i || 0) / prev_i : (prev_i === 0 && (curr_i === undefined || curr_i === null || curr_i === 0) ? 0 : null);

                    if (rrValue === 0) {
                      // Red for churn
                      bgColor = { backgroundColor: "#f8d7da" };
                    }

                    return (
                      <td
                        key={colIdx}
                        id={tooltipId}
                        className="text-center"
                        style={{ ...bgColor, ...fixedWidthStyle, cursor: "help" }}
                      >
                        {rrValue === 0 ? (
                          <UncontrolledTooltip target={tooltipId} placement="top">
                            <div style={{ textAlign: "left" }}>
                              <div><strong>Churn</strong></div>
                              <div><strong>Current window ({customer?.demand_periodicity || 2}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(curr_i || 0)}</div>
                              <div><strong>Previous window ({customer?.demand_periodicity || 2}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(prev_i)}</div>
                              <div style={{ marginTop: "4px", fontSize: "0.85rem" }}>
                                RR = 0%
                              </div>
                            </div>
                          </UncontrolledTooltip>
                        ) : (
                          <>
                            <span style={{ cursor: "help" }}>
                              {(rrValue * 100).toFixed(1) + "%"}
                            </span>
                            <UncontrolledTooltip target={tooltipId} placement="top">
                              <div style={{ textAlign: "left" }}>
                                <div><strong>Current window ({customer?.demand_periodicity || 2}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(curr_i || 0)}</div>
                                <div><strong>Previous window ({customer?.demand_periodicity || 2}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(prev_i)}</div>
                                <div style={{ fontSize: "0.75rem", marginTop: "4px", color: "#666" }}>
                                  Note: Windows overlap by 1 month
                                </div>
                                <div style={{ marginTop: "4px", fontSize: "0.85rem" }}>
                                  {prev_i > 0 ? `RR = ${(rrValue * 100).toFixed(1)}%` : "N/A"}
                                </div>
                              </div>
                            </UncontrolledTooltip>
                          </>
                        )}
                      </td>
                    );
                  } else if (viewMode === "logos") {
                    // Logos view: show LR or LG with background colors
                    // For LR: show if prev_i > 0 (retained or churned)
                    // For LG: show if prev_i === 0 && curr_i > 0 (new logo)

                    let bgColor = {};
                    let tooltipId = '';
                    let tooltipContent = null;

                    // Check reactivation first (can happen with prev_i > 0 or prev_i === 0)
                    if (isReactivationMonth) {
                      tooltipId = `logo-tooltip-${group.organization_id}-${monthKey}`;
                      bgColor = { backgroundColor: "#cfe2ff" }; // Blue for reactivation
                      tooltipContent = (
                        <UncontrolledTooltip target={tooltipId} placement="top">
                          <div style={{ textAlign: "left" }}>
                            <div><strong>Reactivation</strong></div>
                            <div style={{ marginTop: "8px" }}>
                              <div><strong>Current window ({customer?.demand_periodicity || 2}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(curr_i || 0)}</div>
                              {prev_i > 0 && (
                                <div><strong>Previous window ({customer?.demand_periodicity || 2}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(prev_i)}</div>
                              )}
                            </div>
                          </div>
                        </UncontrolledTooltip>
                      );
                    } else if (prev_i > 0) {
                      // Show LR (Logo Retention)
                      const isRetained = curr_i > 0;
                      tooltipId = `lr-tooltip-${group.organization_id}-${monthKey}`;

                      if (isRetained) {
                        // No special color for retained - just show value (consistent with Retention tab)
                        bgColor = {};
                      } else {
                        // Red for churned
                        bgColor = { backgroundColor: "#f8d7da" };
                      }

                      tooltipContent = (
                        <UncontrolledTooltip target={tooltipId} placement="top">
                          <div style={{ textAlign: "left" }}>
                            <div><strong>{isRetained ? "Retained" : "Churned"}</strong></div>
                            <div style={{ marginTop: "8px" }}>
                              <div><strong>Current window ({customer?.demand_periodicity || 2}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(curr_i || 0)}</div>
                              <div><strong>Previous window ({customer?.demand_periodicity || 2}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(prev_i)}</div>
                            </div>
                          </div>
                        </UncontrolledTooltip>
                      );
                    } else if (isOnboardedMonth) {
                      // Show LG (Logo Growth) - new logo (only for onboarded month, consistent with Retention tab)
                      tooltipId = `lg-tooltip-${group.organization_id}-${monthKey}`;
                      // Green for new logo (onboarded)
                      bgColor = { backgroundColor: "#d1e7dd" };

                      tooltipContent = (
                        <UncontrolledTooltip target={tooltipId} placement="top">
                          <div style={{ textAlign: "left" }}>
                            <div><strong>New Logo</strong></div>
                            <div style={{ marginTop: "8px" }}>
                              <div><strong>Current window ({customer?.demand_periodicity || 2}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(curr_i || 0)}</div>
                            </div>
                          </div>
                        </UncontrolledTooltip>
                      );
                    }

                    if (tooltipId) {
                      return (
                        <td
                          key={colIdx}
                          id={tooltipId}
                          className="text-center"
                          style={{ ...bgColor, ...fixedWidthStyle, cursor: "help" }}
                        >
                          {tooltipContent}
                        </td>
                      );
                    }

                    return <td key={colIdx} className="text-center" style={{ ...cellStyle, ...fixedWidthStyle }}></td>;
                  }

                  return <td key={colIdx} style={{ ...cellStyle, ...fixedWidthStyle }}></td>;
                })}
                <td>
                  {currentCartValue > 0 ? (
                    <div className="d-inline-flex align-items-center justify-content-center gap-1">
                      <span>{formatCurrency(currentCartValue)}</span>
                      {currentCartCount > 1 && (
                        <Badge
                          color="light"
                          pill
                          className="border text-muted small px-2"
                        >
                          {currentCartCount}
                        </Badge>
                      )}
                    </div>
                  ) : (
                    "-"
                  )}
                </td>
                <td>
                  {nextCartValue > 0 ? (
                    <div className="d-inline-flex align-items-center justify-content-center gap-1">
                      <span>{formatCurrency(nextCartValue)}</span>
                      {nextCartCount > 1 && (
                        <Badge
                          color="light"
                          pill
                          className="border text-muted small px-2"
                        >
                          {currentCartCount}
                        </Badge>
                      )}
                    </div>
                  ) : (
                    "-"
                  )}
                </td>
                <td
                  className="text-muted text-nowrap"
                  style={{
                    fontSize: "0.8em",
                    // Highlight in blue if churned but has activity in last 1 month
                    ...(viewMode === "revenue" && (() => {
                      const customer = customerLevelMetrics.get(group.organization_id);
                      if (!customer) return {};

                      // Get the most recent month
                      if (months.length === 0) return {};
                      const mostRecentMonth = months[months.length - 1];
                      const monthKey = `${new Date(mostRecentMonth).getFullYear()}-${String(new Date(mostRecentMonth).getMonth() + 1).padStart(2, '0')}`;

                      const prev_i = customer.prev_i?.[monthKey];
                      const curr_i = customer.curr_i?.[monthKey];
                      const isChurned = prev_i > 0 && (curr_i === undefined || curr_i === null || curr_i === 0);

                      // Check if has activity in last 1 month
                      const now = new Date();
                      const oneMonthAgo = new Date(now);
                      oneMonthAgo.setMonth(now.getMonth() - 1);
                      const hasRecentActivity = group.lastActivity && new Date(group.lastActivity) >= oneMonthAgo;

                      if (isChurned && hasRecentActivity) {
                        return { backgroundColor: "#cfe2ff" }; // Blue highlight
                      }
                      return {};
                    })())
                  }}
                  title={
                    group.lastActivity
                      ? new Date(group.lastActivity).toLocaleDateString()
                      : "-"
                  }
                >
                  {group.lastActivity
                    ? getRelativeTime(group.lastActivity)
                    : "-"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {selectedOrgId && (
        <OrgSummaryPane
          organizationId={selectedOrgId}
          onClose={() => setSelectedOrgId(null)}
        />
      )}

      {selectedMonthData && (
        <Offcanvas
          isOpen={true}
          toggle={() => setSelectedMonthData(null)}
          direction="end"
          style={{ width: "40%", top: "0", marginTop: 0 }}
          className="border-start position-fixed"
        >
          <OffcanvasHeader toggle={() => setSelectedMonthData(null)} className="border-bottom pb-2 mb-0">
            {selectedMonthData.customer} - {getMonthYearDisplay(new Date(selectedMonthData.month))}
          </OffcanvasHeader>

          <OffcanvasBody className="p-3">
            <Table bordered responsive hover size="sm">
              <thead>
                <tr>
                  <th className="bg-secondary text-white">Project</th>
                  <th className="bg-secondary text-white text-end">Value</th>
                  <th className="bg-secondary text-white">Booked</th>
                  <th className="bg-secondary text-white">Placed</th>
                </tr>
              </thead>
              <tbody>
                {selectedMonthData.orders.map((order, idx) => (
                  <tr key={idx}>
                    <td>
                      <a
                        href={`/projects/${order.project_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-dark text-decoration-none"
                      >
                        {order.project_name}
                      </a>
                      <span className="text-muted ms-1">#{order.project_id}</span>
                    </td>
                    <td className="text-end">{formatCurrency(order.value)}</td>
                    <td>
                      {order.booked_date
                        ? new Date(order.booked_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })
                        : '-'}
                    </td>
                    <td>
                      {order.placed_date
                        ? new Date(order.placed_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="fw-bold">
                  <td>Total</td>
                  <td className="text-end">
                    {formatCurrency(
                      selectedMonthData.orders.reduce((sum, order) => sum + (order.value || 0), 0)
                    )}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            </Table>
          </OffcanvasBody>
        </Offcanvas>
      )}
    </Row>
  );
};

export default Customers;
